/**
 * SOFT LOCK — Souls-style target selection for the melee game.
 *
 * The contract (user spec, 2026-07-21; switch model revised 2026-07-22):
 *  - HOLDING RMB (focus) with no target ACQUIRES the most likely one: nearest-and-most-
 *    central inside a LARGE cone the player is "somewhat facing".
 *  - AIMING toward another candidate SWITCHES the lock to it (replaces the old F-cycle,
 *    user call): a mouse flick that points the camera decisively at a different enemy —
 *    tight aim cone, must beat the current target's bearing by a clear margin, one hop
 *    per flick (cooldown). The orbit magnetism is capped well below flick speed, so a
 *    deliberate flick always outruns the recentering — the Elden Ring stick-flick, on mouse.
 *  - The lock is STICKY (user call 2026-07-22 — "right click held is no longer the only
 *    way"): once acquired, the target stays selected after the focus hold releases. It
 *    DESELECTS on: the target dying/leaving range, the player deliberately facing AWAY
 *    for a beat (the "quick move away" — a grace timer, not an instant snap), F HELD for
 *    a beat, or Esc (deselectTick). While selected the player is in "face this enemy"
 *    mode — the caller keeps the heading magnetism + strafe stance on without the hold.
 *  - While locked, the caller applies gentle heading MAGNETISM toward the target, so
 *    holding a strafe ORBITS it — the Elden Ring circle. The magnetism is a nudge the
 *    mouse can always override; the lock never owns the camera.
 *
 * This module only SELECTS (pure state machine over candidates); the orbit magnetism and
 * the lock ring are the caller's (main.ts / combat-lab.ts) — rendering and steering stay
 * where rendering and steering live.
 */
import type * as THREE from "three";

/** Acquisition range (m) — deliberately LARGE (the spec's "large range"): lock-on is an
 *  intent declaration, not a hit test. */
const ACQUIRE_RANGE = 30;
/** Keep-range (m): a locked target may drift a bit past acquisition before the lock drops. */
const BREAK_RANGE = 38;
/** Acquisition cone half-angle (rad, ~92°) — "somewhat facing towards". */
const ACQUIRE_CONE = 1.6;
/** Facing-away break: past this bearing (rad, ~126°)… */
const AWAY_CONE = 2.2;
/** …for this long (s) the lock lets go — soft, so an orbit glance never drops it. */
const AWAY_GRACE_S = 0.45;
/** AIM-SWITCH: a different candidate inside this aim cone (rad, ~20°)… */
const SWITCH_CONE = 0.35;
/** …that beats the current lock's bearing by this margin (rad) takes the lock. The margin
 *  is the anti-thrash hysteresis: mid-orbit the current target sits near centre (magnetism),
 *  so only a deliberate flick AWAY from it and ONTO a neighbour clears both gates. */
const SWITCH_MARGIN = 0.22;
/** One hop per flick: switches are rate-limited (s). */
const SWITCH_COOLDOWN_S = 0.25;

export interface LockCandidate {
  id: number;
  /** System/world-space position — a LIVE ref; read immediately. */
  pos: THREE.Vector3;
}

interface Scored {
  c: LockCandidate;
  dist: number;
  /** Signed bearing off camera-forward about `up` (− left, + right). */
  bearing: number;
}

/** F must be HELD this long (s) to deselect — a tap stays interact's. */
const DESELECT_F_HOLD_S = 0.4;

export class SoftLock {
  constructor(private readonly ranges = { acquireRange: ACQUIRE_RANGE, breakRange: BREAK_RANGE }) {}

  targetId: number | null = null;
  private awayT = 0;
  private switchCd = 0;
  private fHoldT = 0;

  /** Drop the lock outright (mode swaps, menus). */
  drop(): void {
    this.targetId = null;
    this.awayT = 0;
    this.fHoldT = 0;
  }

  /** DESELECT GESTURES (the sticky lock's exits, user call 2026-07-22): F held for a
   *  beat, or Esc. Call once per tick from the decision layer that owns the lock; the
   *  "quick move away" exit lives in update() (the away-grace + break-range drops). */
  deselectTick(dt: number, fHeld: boolean, escPressed: boolean): void {
    if (this.targetId === null) { this.fHoldT = 0; return; }
    this.fHoldT = fHeld ? this.fHoldT + dt : 0;
    if (escPressed || this.fHoldT >= DESELECT_F_HOLD_S) this.drop();
  }

  /** Candidates in the acquisition cone, scored. Pure; allocates only on call (per press). */
  private scored(selfPos: THREE.Vector3, up: THREE.Vector3, fwd: THREE.Vector3, cands: Iterable<LockCandidate>): Scored[] {
    // Tangent-plane camera forward.
    const fUp = fwd.x * up.x + fwd.y * up.y + fwd.z * up.z;
    let fx = fwd.x - fUp * up.x, fy = fwd.y - fUp * up.y, fz = fwd.z - fUp * up.z;
    const fl = Math.hypot(fx, fy, fz) || 1;
    fx /= fl; fy /= fl; fz /= fl;
    // Tangent right = fwd × up (right-handed about up).
    const rx = fy * up.z - fz * up.y, ry = fz * up.x - fx * up.z, rz = fx * up.y - fy * up.x;
    const out: Scored[] = [];
    for (const c of cands) {
      const dx = c.pos.x - selfPos.x, dy = c.pos.y - selfPos.y, dz = c.pos.z - selfPos.z;
      const rad = dx * up.x + dy * up.y + dz * up.z;
      const tx = dx - rad * up.x, ty = dy - rad * up.y, tz = dz - rad * up.z;
      const dist = Math.hypot(tx, ty, tz);
      if (dist < 1e-3 || dist > this.ranges.acquireRange || Math.abs(rad) > 12) continue;
      const cf = (tx * fx + ty * fy + tz * fz) / dist;
      const cr = (tx * rx + ty * ry + tz * rz) / dist;
      const bearing = Math.atan2(cr, cf);
      if (Math.abs(bearing) > ACQUIRE_CONE) continue;
      out.push({ c, dist, bearing });
    }
    return out;
  }

  /** The "most likely" target: central-and-near wins (same spirit as the attack assist). */
  private best(list: Scored[]): Scored | null {
    let bestS: Scored | null = null;
    let bestScore = -Infinity;
    for (const s of list) {
      const score = (1 - s.dist / this.ranges.acquireRange) * 0.6 + (1 - Math.abs(s.bearing) / ACQUIRE_CONE);
      if (score > bestScore) { bestScore = score; bestS = s; }
    }
    return bestS;
  }

  /** AIM-SWITCH (replaces the F-cycle): while locked, a candidate the camera points at —
   *  tightly, and decisively more than at the current lock — takes the lock. Called from
   *  update() each tick; pure over the scored list. */
  private aimSwitch(list: Scored[]): void {
    if (this.switchCd > 0) return;
    const cur = list.find((s) => s.c.id === this.targetId);
    if (!cur) return;
    const curAbs = Math.abs(cur.bearing);
    let bestS: Scored | null = null;
    let bestAbs = SWITCH_CONE;
    for (const s of list) {
      if (s.c.id === this.targetId) continue;
      const a = Math.abs(s.bearing);
      if (a < bestAbs && a < curAbs - SWITCH_MARGIN) { bestAbs = a; bestS = s; }
    }
    if (bestS) {
      this.targetId = bestS.c.id;
      this.awayT = 0;
      this.switchCd = SWITCH_COOLDOWN_S;
    }
  }

  /**
   * Per-tick. `focused` = the RMB focus hold — it gates ACQUISITION and the aim-switch
   * retarget only; the lock itself is STICKY and survives the release. Returns the locked
   * candidate (live) or null. Drops on death/range or sustained facing-away (update), and
   * on the explicit gestures (deselectTick).
   */
  update(
    dt: number,
    selfPos: THREE.Vector3,
    up: THREE.Vector3,
    fwd: THREE.Vector3,
    focused: boolean,
    cands: Iterable<LockCandidate>,
  ): LockCandidate | null {
    this.switchCd = Math.max(0, this.switchCd - dt);
    // Validate the current target (alive + in keep-range + not faced-away-too-long).
    if (this.targetId !== null) {
      let live: LockCandidate | null = null;
      for (const c of cands) if (c.id === this.targetId) { live = c; break; }
      if (live) {
        // A flick onto a neighbour hops the lock BEFORE validation — the neighbour is the
        // player's declared intent now (and the flick may have pushed the OLD target past
        // the away cone; switching first keeps that from reading as a drop). Retargeting
        // is FOCUS-gated: a free camera glance must not hop a sticky lock.
        if (focused) this.aimSwitch(this.scored(selfPos, up, fwd, cands));
        if (this.targetId !== live.id) {
          for (const c of cands) if (c.id === this.targetId) { live = c; break; }
        }
        const dx = live.pos.x - selfPos.x, dy = live.pos.y - selfPos.y, dz = live.pos.z - selfPos.z;
        const rad = dx * up.x + dy * up.y + dz * up.z;
        const tx = dx - rad * up.x, ty = dy - rad * up.y, tz = dz - rad * up.z;
        const dist = Math.hypot(tx, ty, tz);
        const fUp = fwd.x * up.x + fwd.y * up.y + fwd.z * up.z;
        const fx = fwd.x - fUp * up.x, fy = fwd.y - fUp * up.y, fz = fwd.z - fUp * up.z;
        const fl = Math.hypot(fx, fy, fz) || 1;
        const cos = (tx * fx + ty * fy + tz * fz) / ((dist || 1) * fl);
        const bearing = Math.acos(Math.max(-1, Math.min(1, cos)));
        this.awayT = bearing > AWAY_CONE ? this.awayT + dt : 0;
        if (dist <= this.ranges.breakRange && this.awayT < AWAY_GRACE_S) return live;
      }
      this.targetId = null; // died / left range / faced away past the grace ("moved away")
      this.awayT = 0;
    }
    // No target: acquire the most likely one — on the FOCUS intent only.
    if (!focused) return null;
    const pick = this.best(this.scored(selfPos, up, fwd, cands));
    if (pick) { this.targetId = pick.c.id; return pick.c; }
    return null;
  }
}
